import {
  Box,
  Button,
  Checkbox,
  Container,
  CSSReset,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Textarea,
} from "@chakra-ui/react";
import { useForm } from "react-hook-form";
import React from "react";
import { ObservatoryBundle } from "../queries";
import { ProductSelect } from "./ProductSelect";
import { DrillDownSelect } from "./DrillDownSelect";
import { MarketArrayReportSelect } from "./MarketArrayReportSelect";
import { SelectController } from "./SelectController";
import { mapBundleItemToOption } from "../helpers";
import { Form, ObservatoryBundleFields } from "./Form";

interface ObservatoryBundleFormProps {
  title: string;
  observatoryBundle?: ObservatoryBundle;
  onSubmit: (data: ObservatoryBundleFields) => void;
  isFormSubmitting?: boolean;
  buttonText?: string;
  buttonLoadingText?: string;
}

export const ObservatoryBundleForm = (props: ObservatoryBundleFormProps) => {
  const {
    title,
    observatoryBundle,
    onSubmit,
    isFormSubmitting,
    buttonText,
    buttonLoadingText,
  } = props;

  const getDefaultFormValues = (
    observatoryBundle?: ObservatoryBundle
  ): ObservatoryBundleFields | undefined => {
    if (!observatoryBundle) {
      return {
        name: "",
        isActive: false,
        description: "",
        marketArrayReport: undefined,
        observatoryReport: undefined,
        insightsReport: undefined,
        drilldown: undefined,
        observatoryBundlesRole: true,
      };
    }

    const {
      name,
      isActive,
      description,
      marketArrayReport,
      observatoryReport,
      insightsReport,
      drilldown,
      roles,
    } = observatoryBundle;

    return {
      name,
      isActive,
      description,
      marketArrayReport: mapBundleItemToOption(marketArrayReport),
      observatoryReport: mapBundleItemToOption(observatoryReport),
      insightsReport: mapBundleItemToOption(insightsReport),
      drilldown: mapBundleItemToOption(drilldown),
      observatoryBundlesRole: !!roles?.includes("ObservatoryBundles"),
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<ObservatoryBundleFields>({
    defaultValues: getDefaultFormValues(observatoryBundle),
  });

  const handleFormSubmit = (data: ObservatoryBundleFields) => {
    onSubmit(data);
  };

  return (
    <Container maxW={1000}>
      <CSSReset />
      <Box
        borderWidth={{ lg: 1 }}
        p={{ lg: 5 }}
        borderRadius={{ lg: 5 }}
        shadow={{ lg: "lg" }}
      >
        <Heading>{title}</Heading>
        <Form control={control} onSubmit={handleSubmit(handleFormSubmit)}>
          <FormControl isRequired mb="4" isInvalid={!!errors.name}>
            <FormLabel>Name</FormLabel>
            <Input
              placeholder="Name"
              {...register("name", { required: "Name is required" })}
            />
          </FormControl>

          <FormControl mb="4" isRequired isInvalid={!!errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              placeholder="Description"
              {...register("description", {
                required: "Description is required",
              })}
            />
          </FormControl>
          <FormControl mb="4" isInvalid={!!errors.isActive} display="flex">
            <FormLabel>Is Active?</FormLabel>
            <Checkbox {...register("isActive")} />
          </FormControl>
          <SelectController label="Observatory Report" name="observatoryReport">
            <ProductSelect
              reportType="ObservatoryReport"
              placeholder="Select an Observatory Report..."
            />
          </SelectController>
          <SelectController label="Insights Report" name="insightsReport">
            <ProductSelect
              reportType="VENN"
              placeholder="Select an Insights Report..."
            />
          </SelectController>
          <SelectController label="Drilldown" name="drilldown">
            <DrillDownSelect />
          </SelectController>
          <SelectController
            label="Market Array Report"
            name="marketArrayReport"
          >
            <MarketArrayReportSelect />
          </SelectController>
          <FormControl mb="4" isInvalid={!!errors.observatoryBundlesRole}>
            <FormLabel>
              Allow users with Observatory Bundle access to view?{" "}
            </FormLabel>
            <Checkbox {...register("observatoryBundlesRole")} />
          </FormControl>
          <Button
            type="submit"
            colorScheme="blue"
            isLoading={isFormSubmitting}
            loadingText={buttonLoadingText || "Submitting"}
            data-cy="submit-button"
          >
            {buttonText || "Submit"}
          </Button>
        </Form>
      </Box>
    </Container>
  );
};
